import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import toast from 'react-hot-toast';
import { Chess } from 'chess.js';
import { db } from '../firebase/config';
import { doc, onSnapshot, updateDoc, arrayUnion } from 'firebase/firestore';
import { PIECE_SETS, BOARD_THEMES, playSound } from './Board/BoardConstants';

// ============================================================
// ⚔️ ARENA BOARD — Live Human vs Human game via Firestore
// Props: matchConfig { gameId, color ('w'|'b'), botName, format }, user, exitMatch
// ============================================================
const parseTime = (val) => {
    if (!val) return Date.now();
    if (typeof val.toDate === 'function') return val.toDate().getTime();
    if (typeof val === 'string') return new Date(val).getTime();
    if (val.seconds !== undefined) return val.seconds * 1000;
    return new Date(val).getTime();
};

export default function ArenaBoard({ matchConfig, user, exitMatch, boardTheme = 'green', pieceTheme = 'cburnett' }) {
    const { gameId, color: playerColor, botName, format: formatId, isSpectator } = matchConfig;

    const [game, setGame] = useState(new Chess());
    const [fen, setFen]   = useState('rnbqkbnr/pppppppp/8/8/8/8/PPPPPPPP/RNBQKBNR w KQkq - 0 1');
    const [moveHistory, setMoveHistory] = useState([]);
    const [fenHistory, setFenHistory] = useState(['rnbqkbnr/pppppppp/8/8/8/8/PPPPPPPP/RNBQKBNR w KQkq - 0 1']);
    const [viewIndex, setViewIndex] = useState(0);
    const [status, setStatus]     = useState('');
    const [isMyTurn, setIsMyTurn] = useState(isSpectator ? false : playerColor === 'w');
    const [whitePlayerName, setWhitePlayerName] = useState('White');
    const [blackPlayerName, setBlackPlayerName] = useState('Black');
    const moveListRef = useRef(null);

    // Dynamic ref tracking for state variables inside closures
    const isInitialLoadRef = useRef(true);
    const prevMovesLengthRef = useRef(0);
    const fenHistoryRef = useRef(fenHistory);
    fenHistoryRef.current = fenHistory;

    // Initial time control parsing
    const initialTime = useMemo(() => {
        if (!formatId) return 300; // Default to 5 minutes
        const parts = formatId.split('_');
        const mins = parseInt(parts[1]);
        if (!isNaN(mins)) return mins * 60;
        return 300;
    }, [formatId]);

    // Clocks and Timer states
    const [wTimeLeft, setWTimeLeft] = useState(initialTime);
    const [bTimeLeft, setBTimeLeft] = useState(initialTime);
    const [lastMoveAt, setLastMoveAt] = useState(null);
    const [activeTurn, setActiveTurn] = useState('w');
    const [gameStatus, setGameStatus] = useState('playing');
    const [lastMove, setLastMove] = useState(null);
    const [confirmResign, setConfirmResign] = useState(false);

    // ── Platform-native board states ──
    const [selectedSq, setSelectedSq] = useState(null);
    const [legalDots, setLegalDots]   = useState([]);
    
    // ── Drag states ──
    const [dragSq, setDragSq]               = useState(null);
    const [dragPos, setDragPos]             = useState(null);
    const boardRef                          = useRef(null);
    const isDraggingRef                     = useRef(false);
    const pointerDownPosRef                 = useRef(null);
    const pointerDownSelectionRef           = useRef(null);
    const pointerDownOffsetRef              = useRef({ x: 0, y: 0 });
    const justDraggedRef                    = useRef(false);

    const [boardWidth, setBoardWidth]       = useState(460);

    const rows = playerColor === 'w' ? ['8','7','6','5','4','3','2','1'] : ['1','2','3','4','5','6','7','8'];
    const cols = playerColor === 'w' ? ['a','b','c','d','e','f','g','h'] : ['h','g','f','e','d','c','b','a'];
    const sqSize = boardWidth / 8;

    useEffect(() => {
        const handleResize = () => {
            const w = Math.max(280, Math.min(window.innerWidth - 32, 480));
            setBoardWidth(w);
        };
        handleResize();
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    // Snapshot values (to tick relative to the last database update)
    const [snapWTime, setSnapWTime] = useState(initialTime);
    const [snapBTime, setSnapBTime] = useState(initialTime);

    useEffect(() => {
        if (moveListRef.current) moveListRef.current.scrollTop = moveListRef.current.scrollHeight;
    }, [moveHistory]);

    // Local ticking clock interval (runs every 100ms for smooth updates)
    useEffect(() => {
        if (gameStatus !== 'playing') return;
        const interval = setInterval(() => {
            if (!lastMoveAt) return;
            const lastMoveTime = parseTime(lastMoveAt);
            const elapsed = Math.max(0, (Date.now() - lastMoveTime) / 1000);
            if (activeTurn === 'w') {
                const newWTime = Math.max(-2, snapWTime - elapsed);
                setWTimeLeft(Math.max(0, newWTime));
                if (playerColor === 'w' && newWTime <= 0) {
                    clearInterval(interval);
                    handleTimeout('w');
                } else if (playerColor === 'b' && newWTime <= -1.5) {
                    clearInterval(interval);
                    handleTimeout('w');
                }
            } else {
                const newBTime = Math.max(-2, snapBTime - elapsed);
                setBTimeLeft(Math.max(0, newBTime));
                if (playerColor === 'b' && newBTime <= 0) {
                    clearInterval(interval);
                    handleTimeout('b');
                } else if (playerColor === 'w' && newBTime <= -1.5) {
                    clearInterval(interval);
                    handleTimeout('b');
                }
            }
        }, 100);
        return () => clearInterval(interval);
    }, [gameStatus, lastMoveAt, activeTurn, snapWTime, snapBTime, playerColor]);

    const handleTimeout = async (lostColor) => {
        await updateDoc(doc(db, 'games', gameId), {
            status: 'timeout',
            lostOnTime: lostColor,
            updatedAt: new Date().toISOString()
        }).catch(err => console.error("Error setting timeout:", err));
    };

    // 🔥 Real-time Firestore listener
    useEffect(() => {
        if (!gameId) return;
        const gameRef = doc(db, 'games', gameId);
        const unsub = onSnapshot(gameRef, (snap) => {
            if (!snap.exists()) return;
            const data = snap.data();
            try {
                // Reconstruct full notation and FEN history from the moves list
                const temp = new Chess();
                const fens = [temp.fen()];
                const movesList = data.moves || [];
                for (let m of movesList) {
                    try {
                        temp.move(m);
                        fens.push(temp.fen());
                    } catch (e2) {}
                }

                setGame(temp);
                setFenHistory(fens);

                // Re-evaluate current view index: auto-scroll to end if player was already at the end
                setViewIndex(prevIdx => {
                    const wasAtEnd = isInitialLoadRef.current || prevIdx >= fenHistoryRef.current.length - 1;
                    const nextIdx = wasAtEnd ? fens.length - 1 : prevIdx;
                    setFen(fens[nextIdx]);
                    return nextIdx;
                });

                setMoveHistory(movesList);
                const myTurn = isSpectator ? false : (data.turn === playerColor);
                setIsMyTurn(myTurn);
                setWhitePlayerName(data.whiteName || data.white || 'White');
                setBlackPlayerName(data.blackName || data.black || 'Black');
                setActiveTurn(data.turn || 'w');
                setGameStatus(data.status || 'playing');

                // Synchronize Clocks
                const dbWTime = data.whiteTime !== undefined ? data.whiteTime : initialTime;
                const dbBTime = data.blackTime !== undefined ? data.blackTime : initialTime;
                setSnapWTime(dbWTime);
                setSnapBTime(dbBTime);
                setWTimeLeft(dbWTime);
                setBTimeLeft(dbBTime);
                setLastMoveAt(data.lastMoveAt || data.matchedAt || data.updatedAt || new Date().toISOString());
                setLastMove(data.lastMove || null);

                // Sound triggers - prevent repeat sounds or initial load sound
                let isNewMove = false;
                if (isInitialLoadRef.current) {
                    prevMovesLengthRef.current = movesList.length;
                } else {
                    isNewMove = movesList.length > prevMovesLengthRef.current;
                    prevMovesLengthRef.current = movesList.length;
                }

                if (isNewMove && data.lastMove) {
                    const lastMoveColor = movesList.length % 2 === 1 ? 'w' : 'b';
                    if (lastMoveColor !== playerColor) {
                        if (temp.isCheckmate()) playSound('notify');
                        else if (data.lastMove.san?.includes('+')) playSound('notify');
                        else if (data.lastMove.san?.includes('x')) playSound('capture');
                        else playSound('move');
                    }
                }
                
                isInitialLoadRef.current = false;

                // Handle Game Statuses
                const pWhiteName = data.whiteName || data.white || 'White';
                const pBlackName = data.blackName || data.black || 'Black';
                
                if (isSpectator) {
                    if (data.status === 'resigned') {
                        const resigner = data.resignedBy === data.white ? pWhiteName : pBlackName;
                        setStatus(`🏳️ ${resigner} resigned.`);
                    } else if (data.status === 'timeout') {
                        const loser = data.lostOnTime === 'w' ? pWhiteName : pBlackName;
                        setStatus(`⏰ ${loser} lost on time.`);
                    } else if (data.status === 'completed') {
                        if (data.reason === 'Checkmate') {
                            const winner = data.winner === 'white' ? pWhiteName : pBlackName;
                            setStatus(`🏆 ${winner} won by checkmate!`);
                        } else {
                            setStatus(`🤝 Draw (${data.reason || 'Agreement'})`);
                        }
                    } else if (temp.isCheckmate()) {
                        const winner = temp.turn() === 'w' ? pBlackName : pWhiteName;
                        setStatus(`🏆 ${winner} won by checkmate!`);
                    } else if (temp.isDraw()) {
                        setStatus("🤝 Draw!");
                    } else if (temp.inCheck()) {
                        const checkedPlayer = temp.turn() === 'w' ? pWhiteName : pBlackName;
                        setStatus(`⚠️ ${checkedPlayer} is in check!`);
                    } else {
                        const activePlayer = temp.turn() === 'w' ? pWhiteName : pBlackName;
                        setStatus(`⏳ ${activePlayer}'s turn`);
                    }
                } else {
                    if (data.status === 'resigned') {
                        setStatus(`${data.resignedBy === user.email ? 'You resigned.' : `${botName} resigned!`}`);
                    } else if (data.status === 'timeout') {
                        setStatus(data.lostOnTime === playerColor ? '💀 You lost on time.' : `🏆 You won on time!`);
                        playSound('notify');
                    } else if (data.status === 'completed') {
                        if (data.reason === 'Checkmate') {
                            const winner = data.winner;
                            const myColorName = playerColor === 'w' ? 'white' : 'black';
                            setStatus(winner === myColorName ? '🏆 You won by checkmate!' : `💀 ${botName} won by checkmate.`);
                        } else {
                            setStatus(`🤝 Draw (${data.reason || 'Agreement'})`);
                        }
                        playSound('notify');
                    } else if (temp.isCheckmate()) {
                        const winnerColor = data.turn === 'w' ? 'b' : 'w';
                        setStatus(winnerColor === playerColor ? '🏆 You won by checkmate!' : `💀 ${botName} won by checkmate.`);
                        playSound('notify');
                    } else if (temp.isDraw()) {
                        setStatus("🤝 Draw!");
                    } else if (temp.inCheck()) {
                        setStatus(myTurn ? '⚠️ You are in check!' : `⚡ ${botName} is in check!`);
                    } else {
                        setStatus(myTurn ? '✅ Your turn' : `⏳ Waiting for ${botName}...`);
                    }
                }
            } catch (e) { console.error('Game state error:', e); }
        });
        return () => unsub();
    }, [gameId, playerColor, botName, user.email, initialTime]);

    // 🎯 Handle piece drop or click move execution
    const attemptMove = (from, to) => {
        if (isSpectator) return false;
        // Can only move if it is my turn, game is not over, and we are viewing the latest move
        const isAtLatestMove = viewIndex === fenHistory.length - 1;
        if (!isMyTurn || game.isGameOver() || !isAtLatestMove || gameStatus !== 'playing') return false;
        
        try {
            const temp = new Chess(fen);
            const move = temp.move({ from, to, promotion: 'q' });
            if (move) {
                const newFen  = temp.fen();
                const newTurn = temp.turn();

                // Calculate time elapsed since last move
                const now = Date.now();
                const lastMoveTime = lastMoveAt ? parseTime(lastMoveAt) : Date.now();
                const elapsed = Math.max(0, Math.floor((now - lastMoveTime) / 1000));

                const updatedWTime = playerColor === 'w' ? Math.max(0, snapWTime - elapsed) : snapWTime;
                const updatedBTime = playerColor === 'b' ? Math.max(0, snapBTime - elapsed) : snapBTime;

                // Sync sound
                if (temp.isCheckmate()) playSound('notify');
                else if (move.captured) playSound('capture');
                else playSound('move');

                // Local optimistic updates
                setGame(temp);
                setFen(newFen);
                setIsMyTurn(false);
                setSelectedSq(null);
                setLegalDots([]);

                // Check game end conditions
                const isCheckmate = temp.isCheckmate();
                const isDraw = temp.isDraw();
                
                let gameEndUpdates = {};
                if (isCheckmate) {
                    gameEndUpdates = {
                        status: 'completed',
                        reason: 'Checkmate',
                        winner: playerColor === 'w' ? 'white' : 'black',
                        result: playerColor === 'w' ? '1-0' : '0-1'
                    };
                } else if (isDraw) {
                    gameEndUpdates = {
                        status: 'completed',
                        reason: temp.isStalemate() ? 'Stalemate' : (temp.isThreefoldRepetition() ? 'Threefold Repetition' : 'Draw'),
                        result: '1/2-1/2'
                    };
                }

                // Sync to Firestore
                updateDoc(doc(db, 'games', gameId), {
                    fen: newFen,
                    turn: newTurn,
                    lastMove: { from, to, san: move.san },
                    moves: arrayUnion(move.san),
                    whiteTime: updatedWTime,
                    blackTime: updatedBTime,
                    lastMoveAt: new Date().toISOString(),
                    updatedAt: new Date().toISOString(),
                    ...gameEndUpdates
                }).catch(() => toast.error('Failed to sync move. Check connection.'));

                return true;
            }
        } catch (e) {}
        return false;
    };

    // ── Helper: get square id from board pixel coords ──
    const pixelToSquare = useCallback((clientX, clientY) => {
        if (!boardRef.current) return null;
        const rect = boardRef.current.getBoundingClientRect();
        const x = clientX - rect.left;
        const y = clientY - rect.top;
        if (x < 0 || y < 0 || x > boardWidth || y > boardWidth) return null;
        const cIdx = Math.floor(x / sqSize);
        const rIdx = Math.floor(y / sqSize);
        if (cIdx < 0 || cIdx > 7 || rIdx < 0 || rIdx > 7) return null;
        return `${cols[cIdx]}${rows[rIdx]}`;
    }, [boardWidth, sqSize, cols, rows]);

    const handleSquareClick = (id) => {
        if (isSpectator) return;
        if (justDraggedRef.current) return;
        const isViewingHistory = viewIndex < fenHistory.length - 1;
        if (isViewingHistory || !isMyTurn || game.isGameOver() || gameStatus !== 'playing') return;
        if (selectedSq) {
            if (id === selectedSq) { setSelectedSq(null); setLegalDots([]); return; }
            if (legalDots.includes(id)) {
                attemptMove(selectedSq, id);
            } else {
                const piece = game.get(id);
                if (piece && piece.color === playerColor) {
                    setSelectedSq(id);
                    setLegalDots(game.moves({ square: id, verbose: true }).map(m => m.to));
                } else {
                    setSelectedSq(null);
                    setLegalDots([]);
                }
            }
        } else {
            const piece = game.get(id);
            if (piece && piece.color === playerColor) {
                setSelectedSq(id);
                setLegalDots(game.moves({ square: id, verbose: true }).map(m => m.to));
            }
        }
    };

    const handlePointerDown = (e, id) => {
        if (isSpectator) return;
        const isViewingHistory = viewIndex < fenHistory.length - 1;
        if (game.isGameOver() || isViewingHistory || !isMyTurn || gameStatus !== 'playing') return;
        const piece = game.get(id);
        if (!piece || piece.color !== playerColor) return;
        
        pointerDownSelectionRef.current = selectedSq;
        isDraggingRef.current = false;
        pointerDownPosRef.current = { x: e.clientX, y: e.clientY };
        
        const rect = boardRef.current.getBoundingClientRect();
        const cursorX = e.clientX - rect.left;
        const cursorY = e.clientY - rect.top;
        
        const cIdx = cols.indexOf(id[0]);
        const rIdx = rows.indexOf(id[1]);
        const pieceLeft = cIdx * sqSize;
        const pieceTop = rIdx * sqSize;
        pointerDownOffsetRef.current = { x: cursorX - pieceLeft, y: cursorY - pieceTop };
        
        setDragSq(id);
        setDragPos({ x: cursorX, y: cursorY });
        
        if (selectedSq !== id) {
            setSelectedSq(id);
            setLegalDots(game.moves({ square: id, verbose: true }).map(m => m.to));
        }
    };

    // 🌐 GLOBAL DRAG-AND-DROP POINTER TRACKING (LICHESS STYLE)
    useEffect(() => {
        if (!dragSq) return;

        const handleGlobalPointerMove = (e) => {
            if (!pointerDownPosRef.current) return;
            const dx = e.clientX - pointerDownPosRef.current.x;
            const dy = e.clientY - pointerDownPosRef.current.y;
            if (Math.abs(dx) > 3 || Math.abs(dy) > 3) {
                isDraggingRef.current = true;
            }
            if (boardRef.current) {
                const rect = boardRef.current.getBoundingClientRect();
                setDragPos({ x: e.clientX - rect.left, y: e.clientY - rect.top });
            }
        };

        const handleGlobalPointerUp = (e) => {
            const targetSq = pixelToSquare(e.clientX, e.clientY);
            const wasDragMove = isDraggingRef.current && targetSq && targetSq !== dragSq;
            const draggedId = dragSq;

            if (isDraggingRef.current) {
                justDraggedRef.current = true;
                setTimeout(() => { justDraggedRef.current = false; }, 50);
            }

            // Clear drag states
            setDragSq(null);
            setDragPos(null);
            pointerDownPosRef.current = null;

            if (wasDragMove && legalDots.includes(targetSq)) {
                attemptMove(draggedId, targetSq);
            }
        };

        window.addEventListener('pointermove', handleGlobalPointerMove);
        window.addEventListener('pointerup', handleGlobalPointerUp);

        return () => {
            window.removeEventListener('pointermove', handleGlobalPointerMove);
            window.removeEventListener('pointerup', handleGlobalPointerUp);
        };
    }, [dragSq, legalDots, pixelToSquare]);

    // Build piece map from display FEN
    const pieceMap = useMemo(() => {
        const map = {};
        const rs = ['8','7','6','5','4','3','2','1'];
        const cs = ['a','b','c','d','e','f','g','h'];
        try {
            new Chess(fen).board().forEach((row, rIdx) => row.forEach((sq, cIdx) => {
                if (sq) map[`${cs[cIdx]}${rs[rIdx]}`] = sq.color + sq.type.toUpperCase();
            }));
        } catch (e) {}
        return map;
    }, [fen]);

    const handleResign = async () => {
        await updateDoc(doc(db, 'games', gameId), {
            status: 'resigned',
            resignedBy: user.email
        }).catch(() => {});
        toast.error('You resigned. Better luck next time!');
        exitMatch();
    };

    // Keyboard navigation (Arrow keys) to browse match moves
    useEffect(() => {
        const handleKeyDown = (e) => {
            // Skip if the user is typing in an input/textarea
            if (document.activeElement && (
                document.activeElement.tagName === 'INPUT' ||
                document.activeElement.tagName === 'TEXTAREA' ||
                document.activeElement.isContentEditable
            )) {
                return;
            }
            
            if (e.key === 'ArrowLeft') {
                setViewIndex(v => {
                    const target = Math.max(0, v - 1);
                    setFen(fenHistory[target]);
                    return target;
                });
            }
            if (e.key === 'ArrowRight') {
                setViewIndex(v => {
                    const target = Math.min(fenHistory.length - 1, v + 1);
                    setFen(fenHistory[target]);
                    return target;
                });
            }
        };
        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [fenHistory]);

    const currentPieceSet = PIECE_SETS[pieceTheme] || PIECE_SETS.cburnett;

    const formatTime = (secs) => {
        const s = Math.max(0, Math.ceil(secs));
        const m = String(Math.floor(s / 60)).padStart(2, '0');
        const sec = String(s % 60).padStart(2, '0');
        return `${m}:${sec}`;
    };

    const opponentName = botName || 'Opponent';
    const myColor      = playerColor === 'w' ? '⬜ White' : '⬛ Black';
    const displayUsername = user?.name || user?.email?.split('@')[0] || 'You';

    const isOpponentTurn = activeTurn === (playerColor === 'w' ? 'b' : 'w');
    const isMyTurnActive = activeTurn === playerColor;
    const { light, dark } = BOARD_THEMES[boardTheme] || BOARD_THEMES.green;

    return (
        <div style={{width: '100%', maxWidth: '1100px', margin: '0 auto', display: 'flex', gap: '24px', alignItems: 'flex-start', flexWrap: 'wrap', justifyContent: 'center' }}>
            {/* Board Column */}
            <div style={{flexShrink: 0, width: boardWidth, display: 'flex', flexDirection: 'column', gap: '10px' }}>

                {/* Opponent Header */}
                <div className="glass-panel" style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 18px', borderRadius: '10px' }}>
                    <div style={{display: 'flex', alignItems: 'center', gap: '12px' }}>
                        <div style={{fontSize: '26px' }}>👤</div>
                        <div>
                            <div style={{color: 'var(--text-primary)', fontWeight: 'bold' }}>{isSpectator ? blackPlayerName : opponentName}</div>
                            <div style={{color: 'var(--text-secondary)', fontSize: '12px' }}>{isSpectator ? '⬛ Black' : (playerColor === 'w' ? '⬛ Black' : '⬜ White')}</div>
                        </div>
                    </div>
                    {/* Opponent Timer */}
                    <div style={{
                        background: isOpponentTurn && gameStatus === 'playing' ? 'rgba(16,185,129,0.2)' : 'rgba(0,0,0,0.3)',
                        padding: '6px 18px',
                        borderRadius: '8px',
                        fontFamily: 'monospace',
                        fontSize: '20px',
                        color: isOpponentTurn && gameStatus === 'playing' ? '#10b981' : 'var(--text-secondary)',
                        fontWeight: 'bold',
                        border: isOpponentTurn && gameStatus === 'playing' ? '1.5px solid #10b981' : '1px solid var(--border-glass)',
                        transition: 'all 0.3s'
                    }}>
                        {formatTime(playerColor === 'w' ? bTimeLeft : wTimeLeft)}
                    </div>
                </div>

                {/* The Board */}
                <div ref={boardRef}
                    style={{
                        width: boardWidth, 
                        height: boardWidth, 
                        position: 'relative', 
                        borderRadius: '6px', 
                        overflow: 'hidden', 
                        boxShadow: '0 12px 40px rgba(0,0,0,0.6)', 
                        cursor: dragSq ? 'grabbing' : 'default', 
                        touchAction: 'none',
                        userSelect: 'none',
                        WebkitUserSelect: 'none',
                        margin: '0 auto'
                    }}>
                    
                    {/* 8x8 squares */}
                    {rows.map((row, rIdx) => cols.map((col, cIdx) => {
                        const id     = `${col}${row}`;
                        const isDark = (rIdx + cIdx) % 2 === 1;
                        const isSelected = selectedSq === id || dragSq === id;
                        const isLastMoveHighlight = lastMove && (lastMove.from === id || lastMove.to === id);
                        const isLegal    = legalDots.includes(id);
                        const hasPiece   = !!pieceMap[id];
                        return (
                            <div key={id}
                                onClick={() => { if (!dragSq) handleSquareClick(id); }}
                                style={{position: 'absolute',
                                    left: cIdx * sqSize, top: rIdx * sqSize,
                                    width: sqSize, height: sqSize,
                                    background: isSelected ? '#f6f669' : (isDark ? dark : light),
                                    boxSizing: 'border-box',
                                    display: 'flex', justifyContent: 'center', alignItems: 'center',
                                }}>
                                {/* Coordinate labels: Rank (1-8) on left files, File (a-h) on bottom rank */}
                                {cIdx === 0 && (
                                    <span style={{
                                        position: 'absolute', 
                                        top: 2, 
                                        left: 3, 
                                        fontSize: '11px', 
                                        color: isDark ? light : dark, 
                                        fontWeight: 'bold', 
                                        pointerEvents: 'none', 
                                        zIndex: 5,
                                        opacity: 0.8
                                    }}>{row}</span>
                                )}
                                {rIdx === 7 && (
                                    <span style={{
                                        position: 'absolute', 
                                        bottom: 1, 
                                        right: 3, 
                                        fontSize: '11px', 
                                        color: isDark ? light : dark, 
                                        fontWeight: 'bold', 
                                        pointerEvents: 'none', 
                                        zIndex: 5,
                                        opacity: 0.8
                                    }}>{col}</span>
                                )}
                                {/* Last move highlight overlay */}
                                {isLastMoveHighlight && !isSelected && (
                                    <div style={{ position: 'absolute', inset: 0, backgroundColor: 'rgba(255, 255, 0, 0.22)', pointerEvents: 'none' }} />
                                )}
                                {/* Legal move dot */}
                                {isLegal && !hasPiece && (
                                    <div style={{width: '34%', height: '34%', background: 'rgba(0,0,0,0.2)', borderRadius: '50%', pointerEvents: 'none' }} />
                                )}
                                {/* Capture ring */}
                                {isLegal && hasPiece && (
                                    <div style={{ position: 'absolute', inset: 0, border: `${Math.max(3, sqSize * 0.07)}px solid rgba(0,0,0,0.25)`, borderRadius: '50%', pointerEvents: 'none' }} />
                                )}
                            </div>
                        );
                    }))}

                    {/* Pieces rendered on top */}
                    {Object.entries(pieceMap).map(([id, piece]) => {
                        const cIdx = cols.indexOf(id[0]);
                        const rIdx = rows.indexOf(id[1]);
                        if (cIdx < 0 || rIdx < 0) return null;
                        
                        const isViewingHistory = viewIndex < fenHistory.length - 1;
                        const isOurPiece = isMyTurn && piece[0] === playerColor && !isViewingHistory && gameStatus === 'playing';
                        const isBeingDragged = dragSq === id;
                        
                        return (
                            <img key={id} src={currentPieceSet[piece]} alt={piece}
                                draggable={false}
                                onDragStart={e => e.preventDefault()}
                                onPointerDown={(e) => {
                                    if (isOurPiece) handlePointerDown(e, id);
                                }}
                                onClick={(e) => {
                                    if (justDraggedRef.current) return;
                                    e.stopPropagation(); // prevent bubbling to square div
                                    if (isOurPiece) {
                                        if (!isDraggingRef.current) {
                                            if (pointerDownSelectionRef.current === id) {
                                                setSelectedSq(null); setLegalDots([]);
                                            } else {
                                                setSelectedSq(id);
                                                setLegalDots(game.moves({ square: id, verbose: true }).map(m => m.to));
                                            }
                                        }
                                    } else {
                                        if (!dragSq) handleSquareClick(id);
                                    }
                                }}
                                style={{position: 'absolute',
                                    left: cIdx * sqSize, top: rIdx * sqSize,
                                    width: sqSize, height: sqSize,
                                    cursor: isOurPiece ? 'grab' : 'default',
                                    zIndex: 10,
                                    opacity: isBeingDragged ? 0.3 : 1,
                                    filter: selectedSq === id ? 'drop-shadow(0 0 8px rgba(255,255,100,0.9))' : 'drop-shadow(0 2px 4px rgba(0,0,0,0.4))',
                                    userSelect: 'none',
                                    WebkitUserSelect: 'none',
                                    pointerEvents: 'auto',
                                    touchAction: 'none',
                                }} />
                        );
                    })}

                    {/* Ghost piece following cursor during drag */}
                    {dragSq && dragPos && pieceMap[dragSq] && (() => {
                        return (
                            <img src={currentPieceSet[pieceMap[dragSq]]} alt="drag"
                                draggable={false}
                                style={{position: 'absolute',
                                    left: dragPos.x - pointerDownOffsetRef.current.x,
                                    top:  dragPos.y - pointerDownOffsetRef.current.y,
                                    width: sqSize, height: sqSize,
                                    pointerEvents: 'none',
                                    zIndex: 9999,
                                    filter: 'drop-shadow(0 4px 12px rgba(0,0,0,0.6))',
                                    userSelect: 'none',
                                    opacity: 0.92,
                                }} />
                        );
                    })()}
                </div>

                {/* My Header */}
                <div className="glass-panel" style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 18px', borderRadius: '10px' }}>
                    <div style={{display: 'flex', alignItems: 'center', gap: '12px' }}>
                        <div style={{fontSize: '26px' }}>🎓</div>
                        <div>
                            <div style={{color: 'var(--text-primary)', fontWeight: 'bold' }}>{isSpectator ? whitePlayerName : displayUsername}</div>
                            <div style={{color: 'var(--text-secondary)', fontSize: '12px' }}>{isSpectator ? '⬜ White' : myColor}</div>
                        </div>
                    </div>
                    {/* My Timer */}
                    <div style={{
                        background: isMyTurnActive && gameStatus === 'playing' ? 'rgba(59,130,246,0.2)' : 'rgba(0,0,0,0.3)',
                        padding: '6px 18px',
                        borderRadius: '8px',
                        fontFamily: 'monospace',
                        fontSize: '20px',
                        color: isMyTurnActive && gameStatus === 'playing' ? '#3b82f6' : 'var(--text-secondary)',
                        fontWeight: 'bold',
                        border: isMyTurnActive && gameStatus === 'playing' ? '1.5px solid #3b82f6' : '1px solid var(--border-glass)',
                        transition: 'all 0.3s'
                    }}>
                        {formatTime(playerColor === 'w' ? wTimeLeft : bTimeLeft)}
                    </div>
                </div>
            </div>

            {/* Right Panel */}
            <div className="glass-panel" style={{flex: 1, minWidth: '260px', maxWidth: '360px', display: 'flex', flexDirection: 'column', borderRadius: '12px', overflow: 'hidden', height: boardWidth + 100 }}>
                {/* Status */}
                <div style={{background: 'rgba(0,0,0,0.3)', padding: '14px 18px', borderBottom: '1px solid var(--border-glass)', fontWeight: 'bold', color: status.includes('won') ? '#10b981' : status.includes('check') || status.includes('💀') || status.includes('resigned') ? '#ef4444' : 'var(--text-primary)', fontSize: '14px', textAlign: 'center' }}>
                    {viewIndex < fenHistory.length - 1 ? '👀 Reviewing History' : (status || '⚔️ Game in Progress')}
                </div>

                {/* Move List */}
                <div ref={moveListRef} style={{flex: 1, overflowY: 'auto', padding: '12px', display: 'flex', alignContent: 'flex-start', flexWrap: 'wrap', gap: '6px' }}>
                    {moveHistory.length === 0
                        ? <span style={{color: '#475569', fontSize: '13px', fontStyle: 'italic', margin: 'auto' }}>Waiting for first move...</span>
                        : moveHistory.reduce((result, v, i, arr) => { if (i % 2 === 0) result.push(arr.slice(i, i + 2)); return result; }, []).map((pair, idx) => {
                            const wIdx = idx * 2 + 1;
                            const bIdx = idx * 2 + 2;
                            return (
                                <div key={idx} style={{display: 'flex', gap: '5px', fontSize: '14px', alignItems: 'center', background: 'var(--neu-inner)', padding: '4px 8px', borderRadius: '6px', width: '100%' }}>
                                    <span style={{color: '#f59e0b', fontWeight: 'bold', width: '28px', flexShrink: 0 }}>{idx + 1}.</span>
                                    <span 
                                        onClick={() => { setViewIndex(wIdx); setFen(fenHistory[wIdx]); }} 
                                        style={{color: viewIndex === wIdx ? 'white' : 'var(--text-primary)', fontWeight: 'bold', flex: 1, cursor: 'pointer', background: viewIndex === wIdx ? '#3b82f6' : 'transparent', padding: '2px 5px', borderRadius: '4px', transition: 'all 0.15s' }}
                                    >
                                        {pair[0]}
                                    </span>
                                    {pair[1] && (
                                        <span 
                                            onClick={() => { setViewIndex(bIdx); setFen(fenHistory[bIdx]); }} 
                                            style={{color: viewIndex === bIdx ? 'white' : 'var(--text-primary)', fontWeight: 'bold', flex: 1, cursor: 'pointer', background: viewIndex === bIdx ? '#3b82f6' : 'transparent', padding: '2px 5px', borderRadius: '4px', transition: 'all 0.15s' }}
                                        >
                                            {pair[1]}
                                        </span>
                                    )}
                                </div>
                            );
                        })
                    }
                </div>

                {/* Timeline Controls */}
                <div style={{background: 'rgba(0,0,0,0.2)', padding: '10px', display: 'flex', justifyContent: 'center', gap: '12px', borderTop: '1px solid var(--border-glass)' }}>
                    <button onClick={() => { setViewIndex(0); setFen(fenHistory[0]); }} className="neu-btn" style={{padding: '8px 14px', fontSize: '16px' }} title="Start">⏮</button>
                    <button onClick={() => setViewIndex(v => { const next = Math.max(0, v - 1); setFen(fenHistory[next]); return next; })} className="neu-btn" style={{padding: '8px 14px', fontSize: '16px' }} title="Back">◀</button>
                    <button onClick={() => setViewIndex(v => { const next = Math.min(fenHistory.length - 1, v + 1); setFen(fenHistory[next]); return next; })} className="neu-btn" style={{padding: '8px 14px', fontSize: '16px' }} title="Forward">▶</button>
                    <button onClick={() => { const end = fenHistory.length - 1; setViewIndex(end); setFen(fenHistory[end]); }} className="neu-btn" style={{padding: '8px 14px', fontSize: '16px' }} title="End">⏭</button>
                </div>

                {/* Action Buttons */}
                <div style={{padding: '14px', display: 'flex', gap: '10px', borderTop: '1px solid var(--border-glass)' }}>
                    <button
                        onClick={() => { navigator.clipboard.writeText(game.pgn()); toast.success('PGN Copied!'); }}
                        className="neu-btn"
                        style={{flex: 1, padding: '10px', fontSize: '13px' }}
                    >
                        📋 Copy PGN
                    </button>
                    {!isSpectator && (confirmResign ? (
                        <button
                            onClick={handleResign}
                            className="neu-btn"
                            style={{flex: 1, padding: '10px', color: 'white', background: '#ef4444', fontSize: '13px', fontWeight: 'bold' }}
                        >
                            Confirm?
                        </button>
                    ) : (
                        <button
                            onClick={() => { setConfirmResign(true); setTimeout(() => setConfirmResign(false), 4000); }}
                            className="neu-btn"
                            style={{flex: 1, padding: '10px', color: '#ef4444', fontSize: '13px' }}
                            disabled={game.isGameOver() || gameStatus !== 'playing'}
                        >
                            🏳️ Resign
                        </button>
                    ))}
                    <button
                        onClick={exitMatch}
                        className="neu-btn"
                        style={{flex: 1, padding: '10px', fontSize: '13px' }}
                    >
                        ✖ Exit
                    </button>
                </div>
            </div>
        </div>
    );
}
